import {
  Compass,
  LinkedinLogo,
  Plugs,
  User,
  UserList,
} from "@phosphor-icons/react";
import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import LicenseIcon from "assets/icons/license.svg?react";
import RentedIcon from "assets/icons/rented.svg?react";

interface WorkspaceAccountSelectorProps<Selection extends string[] | string> {
  selection: Selection;
  setSelection: (selection: Selection) => void;
  isNavigatorOnly?: boolean;
  className?: string;
}

export default function WorkspaceAccountSelector<
  Selection extends string[] | string,
>({
  selection,
  setSelection,
  isNavigatorOnly = false,
  className = null,
}: WorkspaceAccountSelectorProps<Selection>) {
  const [searchInputValue, setSearchInputValue] = useState("");
  const { accounts: workspaceAccounts } = useSelectedWorkspaceContext();

  let accounts = workspaceAccounts;
  if (isNavigatorOnly) {
    accounts = workspaceAccounts.filter(
      ({ sales_navigator }) => sales_navigator,
    );
  }

  const isMultiSelect = Array.isArray(selection);

  function removeAccountId(accountId: string) {
    if (isMultiSelect) {
      setSelection(selection.filter((id) => id !== accountId) as Selection);
    }
  }
  function addAccountId(accountId: string) {
    setSelection([...selection, accountId] as Selection);
  }
  let triggerLeftIcon = isMultiSelect ? <UserList /> : <User />;
  let triggerLabel = isMultiSelect ? "All accounts" : "Select account";

  if (
    isMultiSelect
      ? selection.length && selection.length !== accounts.length
      : !!selection
  ) {
    triggerLeftIcon = (
      <div className="flex shrink-0 flex-row -space-x-1.5 rtl:space-x-reverse">
        {(typeof selection === "string"
          ? [selection]
          : selection.slice(0, 3)
        ).map((selectedAccountId) => {
          const account = accounts.find(({ id }) => id === selectedAccountId);

          return (
            <ProfileImage
              key={account.id}
              size="xs"
              className="ring-1 ring-purple-50"
              src={account.picture_url}
            />
          );
        })}
      </div>
    );

    triggerLabel =
      isMultiSelect && selection.length > 1
        ? `${selection.length} Accounts`
        : accounts.find(
            ({ id }) => id === (isMultiSelect ? selection[0] : selection),
          ).full_name;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterButton leftIcon={triggerLeftIcon} className={className}>
          {triggerLabel}
        </FilterButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="min-w-[--radix-popper-anchor-width] z-aboveDialog w-60"
        align="end"
      >
        {workspaceAccounts.length > 5 && (
          <MenuSearchInput
            placeholder="Search accounts"
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
          />
        )}
        <DropdownMenuList>
          {isMultiSelect && (
            <DropdownMenuItem onSelect={() => setSelection([] as Selection)}>
              All accounts
            </DropdownMenuItem>
          )}
          {accounts
            .filter(({ full_name }) =>
              searchInputValue
                ? full_name
                    .toLowerCase()
                    .includes(searchInputValue.toLowerCase())
                : true,
            )
            .map((account) => {
              const {
                id,
                full_name: fullName,
                picture_url: pictureUrl,
                license,
                premium: isPremium,
                sales_navigator: isSalesNavigator,
                state,
                type,
              } = account;

              const isLoggedIn = state === "LoggedIn";

              return (
                <DropdownMenuItem
                  key={id}
                  onSelect={() => {
                    setSelection(
                      (typeof selection === "string" ? id : [id]) as Selection,
                    );
                  }}
                >
                  <RenderIf condition={isMultiSelect}>
                    <DropdownMenuCheckbox
                      isChecked={selection.includes(id)}
                      onCheckedChange={() => {
                        if (selection.includes(id)) {
                          removeAccountId(id);
                        } else {
                          addAccountId(id);
                        }
                      }}
                    />
                  </RenderIf>

                  <div className="relative shrink-0">
                    <ProfileImage size="xs" src={pictureUrl} />

                    {type === "rental" ? (
                      <RentedIcon className="absolute -bottom-1 -right-[5px]" />
                    ) : (
                      <RenderIf condition={!!license}>
                        <LicenseIcon className="absolute -bottom-1 -right-[5px]" />
                      </RenderIf>
                    )}
                  </div>

                  <span className="line-clamp-1 break-all">{fullName}</span>

                  {isLoggedIn ? (
                    <>
                      <RenderIf condition={isPremium}>
                        <LinkedinLogo
                          weight="fill"
                          className="!size-4 fill-yellow-500"
                        />
                      </RenderIf>

                      <RenderIf condition={isSalesNavigator}>
                        <Compass
                          weight="fill"
                          className="!size-4 fill-blue-500"
                        />
                      </RenderIf>
                    </>
                  ) : (
                    <Plugs weight="fill" className="!size-4 fill-black-400" />
                  )}
                </DropdownMenuItem>
              );
            })}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
